/**
 * Pure logic for `CdwSsrmGridComponent`'s per-instance grid session ID.
 * Extracted so it can be unit-tested without importing the full component
 * (which pulls in `AgGridModule` and breaks under vitest's ESM module
 * system — see `sql-ssrm-datasource.factory.spec.ts` header for the same
 * constraint).
 */

/**
 * sessionStorage key prefix for a grid's session UUID (`grid-session:{viewKey}`).
 * Exported so the app's lifecycle cleanup (`clearGridSessions`) matches the
 * same keys this file writes.
 */
export const GRID_SESSION_KEY_PREFIX = 'grid-session:';

/**
 * Mint a new session identifier. Prefers `crypto.randomUUID`; falls back to
 * `crypto.getRandomValues` (available anywhere `crypto` is, even without
 * `randomUUID`) rather than `Math.random`, which CodeQL flags as insecure
 * randomness once the value flows into a session identifier header.
 */
export function generateSessionId(cryptoObj: Crypto | undefined = globalThis.crypto): string {
  if (cryptoObj && typeof cryptoObj.randomUUID === 'function') {
    return cryptoObj.randomUUID();
  }
  if (cryptoObj && typeof cryptoObj.getRandomValues === 'function') {
    const bytes = cryptoObj.getRandomValues(new Uint8Array(16));
    return `${Date.now()}-${Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('')}`;
  }
  return `${Date.now()}`;
}

/**
 * Read or mint the session UUID for `viewKey`. Stable within a tab across
 * reloads (sessionStorage); distinct across tabs (sessionStorage scope);
 * distinct across viewKeys (key includes viewKey). Returns empty string if
 * sessionStorage is unavailable — the data service then skips the
 * `x-cdw-grid-session` header and the backend takes its non-selection-aware
 * code path (full backwards compat).
 */
export function mintOrRestoreSessionId(
  viewKey: string,
  storage: Storage | undefined = typeof sessionStorage !== 'undefined' ? sessionStorage : undefined,
  cryptoObj: Crypto | undefined = globalThis.crypto
): string {
  if (!viewKey) return '';
  try {
    if (!storage) return '';
    const storageKey = `${GRID_SESSION_KEY_PREFIX}${viewKey}`;
    const existing = storage.getItem(storageKey);
    if (existing) return existing;
    const minted = generateSessionId(cryptoObj);
    storage.setItem(storageKey, minted);
    return minted;
  } catch {
    // sessionStorage unavailable (private browsing, SSR, sandboxed iframe).
    // Returning empty disables the selection-aware code path for this grid.
    return '';
  }
}
